import type { BrandStyleTokens } from '~/design-system/components/DeckGen/ai/deckFlowPrompt'
import type { BrandStyle } from '~/design-system/components/DeckGen/types/presentation'
import { brandKitToGenerationInputs, type BrandKit, type BrandKitGenerationInputs } from './brandKit'
import { brandKitFromDto, brandKitToPatch, type BrandKitDto, type BrandKitSummaryDto, type StoredBrandKit } from './brandKitWire'
import type { MoodboardProfile } from '~/design-system/components/DeckGen/types/presentation'
import type { BakeBrandLogo } from '~/design-system/components/DeckGen/utils/bakeFlowHtml'
import { brandFaceCss, ensureBrandKitFonts } from '~/design-system/components/DeckGen/utils/brandFonts'
import { brandKitFromBinding } from '../style/styleForArtifact'
import { stripPageIndex } from '../kitReferences/kitPages'
import { stripKitDocument } from '../documentRead/kitImport'
import { stripReferences } from '../kitReferences/kitReferences'
import { stripSiteCapture } from '../kitReferences/kitSite'
import { stripTraces } from '../kitReferences/kitTraces'
import { registerDocFonts } from './docFonts'

/**
 * The DEFAULT-kit half of the plan's §4 promise: "pick one when you generate, or the default applies."
 *
 * Resolution order at every generation seam is, and must stay:
 *   wired style images → the deck's ACTIVE design system → the deck's kit BINDING → the user's DEFAULT
 *   brand kit → nothing.
 * The default kit is a FALLBACK, never an override: a deck that already carries a style keeps it, and a
 * user who explicitly picked "no style" (designSystemId === null) gets none — the kit must not sneak back
 * in through that door, or "I turned the style off" stops being true.
 *
 * ONE RESOLVER OWNS THAT ORDER NOW: {@link resolveKitForGeneration}. Every tier reads the kit LIVE and
 * falls back to what the deck already holds when the read fails, so /deck and /canvas cannot disagree
 * about which kit styles a generation or how fresh it is (2026-09-05, they did — see the resolver).
 *
 * Every failure path answers null and generation proceeds exactly as it does today. A dead backend, a
 * signed-out session, malformed payloads — none of them may cost the user their deck.
 */

/**
 * The user's default kit, fully hydrated, or null.
 *
 * Two requests by design: the list endpoint ships summaries without payloads (that is its contract), so
 * finding WHICH kit is the default is the list's job and fetching its style is the detail route's job.
 *
 * `publish: false` reads the kit and leaves the artifacts' faces as they are: for a caller that wants one
 * fact from it (a new text box's font) and must not switch every open artifact to this kit's files.
 */
export async function fetchDefaultBrandKit({ publish = true }: { publish?: boolean } = {}): Promise<StoredBrandKit | null> {
  try {
    const listResponse = await fetch('/brand-kits-api')
    if (!listResponse.ok) return null
    const summaries = (await listResponse.json()) as BrandKitSummaryDto[]
    const defaultId = Array.isArray(summaries) ? summaries.find((s) => s?.isDefault)?.id : undefined
    if (!defaultId) return null
    const kitResponse = await fetch(`/brand-kits-api/${defaultId}`)
    if (!kitResponse.ok) return null
    const kit = brandKitFromDto((await kitResponse.json()) as BrandKitDto)
    if (publish) publishBrandFonts(kit)
    return kit
  } catch (e) {
    console.warn('[brandKit] default-kit lookup failed, generating without it', e)
    return null
  }
}

/**
 * Hand a kit's real font files to the artifact documents.
 *
 * A kit imported from a website stores that site's own `@font-face` files, and no provider hosts them —
 * so unless something publishes them, every artifact renders the brand's typeface as a system fallback
 * while correctly displaying its name. Called from the default-kit fetch because that is the one seam
 * every generation path already goes through, so an artifact generated from the default kit carries its
 * faces with no change to the generation hooks (two of which other sessions hold tonight).
 *
 * Deliberately not an override: this publishes what the fallback kit ships, and the kit PAGE publishes
 * whichever kit you open. A kit selected as a deck's active design system but never opened and never the
 * default is the remaining gap, and it needs a call from the generation hooks to close.
 */
export function publishBrandFonts(kit: StoredBrandKit | null | undefined) {
  if (!kit) return
  // BOTH SURFACES, and they need different things. `ensureBrandKitFonts` does the pair:
  //
  //   the LIVE artifact — a sandboxed iframe on an opaque origin — gets the faces from the registry,
  //   injected into its own document by `withDocBridge`, pointed at the brand's own CDN.
  //
  //   the RESTING artifact — a baked HTML string in a SHADOW ROOT — cannot. A shadow tree shares the
  //   HOST document's FontFaceSet, and the bake only reports `link[rel=stylesheet]` hrefs for the parent
  //   to register (docBridge's `fontHrefs`). Our faces are an inline <style>, so they were never in that
  //   list and the resting card fell back to a system font while the open editor showed the real one —
  //   the same artifact rendering two different ways depending on whether you had it open.
  //
  // So the faces are registered on the host document too. Through the PROXY here, deliberately: this is
  // our own page, same-origin and authed, and most font hosts send no CORS header.
  ensureBrandKitFonts(kit.brandStyle?.fontFaces, [kit.profile?.headingFont, kit.profile?.bodyFont])
  registerBrandFontsForRaster(kit)
}

/**
 * THE THIRD SURFACE: the far-view THUMBNAIL RASTER.
 *
 * Guri, on his own canvas: "at 15% zoom the fonts are not loading correctly, but when I zoom in, it
 * loads correctly. It's from the brand kit." Two tiers render an artifact and a third rasterizes it, and
 * the third had no route to these faces at all:
 *
 *   live iframe   — opaque origin, carries its own `@font-face` (docBridge). Works.
 *   resting card  — shadow root, shares the HOST document's FontFaceSet (`ensureBrandKitFonts`). Works.
 *   FAR VIEW      — a bitmap baked through a sandboxed SVG that CANNOT FETCH, so `docFonts` inlines every
 *                   face as a data: URL first. It builds that list from the bake's `link[rel=stylesheet]`
 *                   hrefs, and our faces are an inline `<style>` — invisible to it. The bitmap therefore
 *                   baked with fallback glyphs, which is exactly the type change he sees on zoom-out.
 *
 * WHY A data: URL AND NOT A ROUTE. The obvious fix is to serve this css from an endpoint and link it, so
 * the bake reports it like any sheet. But a `<link>` inside the artifact would have to be reachable from
 * an OPAQUE origin — measured this session: the sandboxed iframe cannot reach our authed proxy at all
 * ("Failed to fetch"), so that route would have to be unauthenticated. A data: URL removes the endpoint,
 * and with it the security question: `ingestStylesheet` only does `fetch(href)`, which resolves a data:
 * URL to the same text (verified: 200, byte-identical round trip).
 *
 * The urls INSIDE stay direct-to-CDN. `inlineBinaries` fetches them from the MAIN document, where a
 * cross-origin font needs the brand's CORS header — and any brand whose font renders in the iframe at all
 * already sends one, because the iframe accepts nothing else. The iframe's constraint is the tighter of
 * the two, so nothing reaches here that it could not already handle.
 *
 * Idempotent by construction: `registerDocFonts` dedupes on href, and the href IS the css, so an
 * unchanged kit is a no-op while an edited one is a genuinely new entry that re-inlines. That also makes
 * the thumb-cache key (`fontcss:<href>`) self-invalidating, which a stable route url would not have been.
 */
export function brandFontsRasterHref(kit: StoredBrandKit | null | undefined): string | null {
  const css = brandFaceCss(kit?.brandStyle?.fontFaces, [kit?.profile?.headingFont, kit?.profile?.bodyFont], 'direct')
  if (!css) return null
  try {
    // `unescape(encodeURIComponent(...))` because btoa throws on any non-Latin-1 character, and a font
    // family name is free text that can carry one.
    return `data:text/css;base64,${btoa(unescape(encodeURIComponent(css)))}`
  } catch (e) {
    console.warn('[brandKit] could not build the raster font href', e)
    return null
  }
}

/** The impure half: hand that href to the raster pipeline. Split from the builder above so the part with
 *  the real logic stays a pure unit this repo's node-environment tests can reach without a DOM. */
function registerBrandFontsForRaster(kit: StoredBrandKit) {
  if (typeof window === 'undefined') return
  const href = brandFontsRasterHref(kit)
  if (href) registerDocFonts([href])
}

let primed = false

/**
 * Fetch the default kit purely to publish its faces. For render surfaces that show artifacts without
 * generating one — opening a canvas, opening the artifact editor — where nothing else would ever ask for
 * a kit. Fire-and-forget: a failure costs the brand font, never the render.
 *
 * ONCE PER SESSION, and that guard is load-bearing rather than tidiness: `withDocBridge` calls this on
 * every artifact render that finds no faces published, so a canvas of forty artifacts would otherwise put
 * forty identical lookups on the wire during the first paint. A real kit change goes through
 * `publishBrandFonts` (generation) or the kit page, both of which set faces directly.
 */
export function primeBrandFonts(): void {
  if (primed) return
  primed = true
  void fetchDefaultBrandKit()
}

/**
 * How long one kit read is reused, in ms.
 *
 * Every generation re-reads its kit live now (see {@link resolveKitForGeneration}), and the two places
 * that generate in bursts — the toolbar's 4× variants row and the whole-deck loops — would otherwise put
 * four to twenty identical reads on the wire inside a second. Five seconds is long enough to cover one
 * burst and short enough that an edit on the kit page reaches the next deliberate generation.
 */
export const KIT_READ_MEMO_MS = 5_000

/** In-flight and recent reads by kit id. The PROMISE is held, not the row, so a burst shares one request. */
const kitReads = new Map<string, { at: number; read: Promise<StoredBrandKit | null> }>()

/**
 * Drop memoised reads — one kit, or all. Called after a write here so a pin followed by a generation
 * inside the window sees the reference it just stored; tests use it to start clean.
 */
export function forgetBrandKitReads(kitId?: string): void {
  if (kitId) kitReads.delete(kitId)
  else kitReads.clear()
}

/** One kit by id, hydrated, or null. Same guards as the default lookup: every failure is a null and the
 *  caller carries on with whatever it already had. Memoised for {@link KIT_READ_MEMO_MS} per id. */
export function fetchBrandKitById(kitId: string): Promise<StoredBrandKit | null> {
  const now = Date.now()
  const held = kitReads.get(kitId)
  if (held && now - held.at < KIT_READ_MEMO_MS) return held.read
  const read = readBrandKit(kitId).then((kit) => {
    // A MISS IS NOT REMEMBERED. A null is a signed-out tab, a dead backend or a kit that was just created
    // and not yet readable, and none of those is a fact worth repeating for five seconds: the next
    // generation should try again. Only this read's own entry is cleared, never a newer one.
    if (!kit && kitReads.get(kitId)?.read === read) kitReads.delete(kitId)
    return kit
  })
  kitReads.set(kitId, { at: now, read })
  return read
}

async function readBrandKit(kitId: string): Promise<StoredBrandKit | null> {
  try {
    const res = await fetch(`/brand-kits-api/${kitId}`)
    if (!res.ok) return null
    return brandKitFromDto((await res.json()) as BrandKitDto)
  } catch (e) {
    console.warn('[brandKit] kit lookup failed', e)
    return null
  }
}

/**
 * Write one kit's profile and return the row as the server now holds it, or null.
 *
 * For the seams that have no kits hook (the deck workspace: `useBrandKits` hydrates every kit, which
 * is the wrong price for one pin). Same wire shape the kit page sends, so a profile written here and
 * one written there cannot disagree about what a profile is on the wire.
 */
export async function patchBrandKitProfile(kitId: string, profile: MoodboardProfile): Promise<StoredBrandKit | null> {
  try {
    const res = await fetch(`/brand-kits-api/${kitId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(brandKitToPatch({ profile })),
    })
    if (!res.ok) return null
    // The row just changed under any read still inside the memo window; the next generation must see it.
    forgetBrandKitReads(kitId)
    return brandKitFromDto((await res.json()) as BrandKitDto)
  } catch (e) {
    console.warn('[brandKit] profile write failed', e)
    return null
  }
}

/**
 * The binding a deck should carry after a generation resolved its kit.
 *
 * TWO CASES, ONE RULE EACH.
 *
 * LIVE — the kit was read from the server just now: the binding becomes a FRESH SNAPSHOT of that kit,
 * whole. This is the reversal of what `backfillBindingFields` used to do here, and it is deliberate.
 * Guri, 2026-09-05, on a deck whose kit he had edited: "when I regenerate a grid… everything looks
 * exactly the same." The snapshot's job was "editing a kit never changes a deck you already made", and
 * it still is — for the DOCUMENTS already on the deck, which are never re-rendered. It was never meant to
 * make the NEXT generation ignore the kit, and refreshing on every landing is what makes a kit edit reach
 * the next artifact on /canvas the way it already did on /deck.
 *
 * NOT LIVE — the read failed (public share, signed out, a deleted kit) and generation is running on what
 * the deck holds: the binding keeps every value it recorded and gains only what it never had, from a
 * stored kit carrying the same id. Filling an ABSENT field is not overwriting a recorded one; a field that
 * did not exist when the snapshot was taken recorded no choice to protect (the 2026-08-18 type-scale
 * gap). A stored kit with a DIFFERENT id fills nothing, so a deck bound to an old kit cannot silently
 * acquire another brand's type system through this door.
 *
 * Pure. The fetch is the resolver's job.
 */
export function refreshBinding(
  binding: BrandKitBinding | null | undefined,
  from: { live: StoredBrandKit } | { stored: BrandKit | null | undefined },
): BrandKitBinding | null {
  if ('live' in from) return bindingFromKit(from.live, storedKitToGenerationInputs(from.live))
  if (!binding) return null
  const stored = from.stored
  if (!stored || stored.id !== binding.kitId) return binding
  if (binding.typeScale && binding.fontFaces?.length) return binding
  const typeScale = binding.typeScale ?? stored.brandStyle?.typeScale
  const fontFaces = binding.fontFaces?.length ? binding.fontFaces : stored.brandStyle?.fontFaces
  if (!typeScale && !fontFaces?.length) return binding
  return {
    ...binding,
    ...(typeScale ? { typeScale } : {}),
    ...(fontFaces?.length ? { fontFaces } : {}),
  }
}

/** Where the kit a generation runs on came from. `snapshot` is a stored fallback after a failed read. */
export type KitSource = 'live' | 'snapshot' | 'default' | 'none'

export interface KitResolution {
  /** The kit to style this generation with, or null. A `StoredBrandKit` on `live` and `default`. */
  kit: BrandKit | null
  /** The prompt block and tokens projected from `kit`, once, so the artifact paths need no second projection
   *  and cannot pick the wrong one (a stored kit has no logo column to merge; a live one does). */
  inputs: BrandKitGenerationInputs | null
  /** The binding the deck should carry after this generation lands: a fresh snapshot on a live read,
   *  the deck's own binding (possibly filled) on a fallback, null when no kit applies. Compare it to the
   *  binding you passed in before stamping — an unchanged one costs a write for nothing. */
  binding: BrandKitBinding | null
  source: KitSource
}

const NO_KIT: KitResolution = { kit: null, inputs: null, binding: null, source: 'none' }

/**
 * ONE TRUTH FOR WHICH KIT, HOW FRESH. Every generation surface calls this and nothing else.
 *
 * WHY IT EXISTS. On 2026-09-05 the two surfaces disagreed: /deck re-read the live kit on every
 * generation while /canvas generated from its snapshot and only backfilled absent fields, so a kit edit
 * showed on one and not the other; whole-deck generation on /canvas skipped the binding altogether. The
 * tiers below are the artifact path's own order with the live read put on every tier and the fallback made
 * explicit:
 *
 *   1. `optOut`         — the deck chose "no kit, its own look". Nothing, not even the default.
 *   2. the ACTIVE kit   — read live by id; the stored design system when the read fails.
 *   3. the BINDING      — read live by the kit it names; `brandKitFromBinding` when the read fails, which
 *                         is byte-for-byte what a public share or a signed-out tab generated with before.
 *   4. the DEFAULT kit  — the account's default, when the deck has no kit anywhere.
 *
 * A live read publishes the kit's font files (the resting card and the far-view raster need them on the
 * host document) and REFRESHES the binding to a fresh snapshot — see {@link refreshBinding} for why that
 * is right. A failed read never throws: the fallback is what the deck already had.
 */
export async function resolveKitForGeneration(input: {
  activeKit?: BrandKit | null
  binding?: BrandKitBinding | null
  optOut?: boolean
}): Promise<KitResolution> {
  if (input.optOut) return NO_KIT
  const binding = input.binding ?? null

  if (input.activeKit) {
    const live = await fetchBrandKitById(input.activeKit.id)
    if (live) return landed(live, binding, 'live')
    return {
      kit: input.activeKit,
      inputs: brandKitToGenerationInputs(input.activeKit),
      binding: refreshBinding(binding, { stored: input.activeKit }),
      source: 'snapshot',
    }
  }

  if (binding) {
    const live = await fetchBrandKitById(binding.kitId)
    if (live) return landed(live, binding, 'live')
    const kit = brandKitFromBinding(binding)
    return {
      kit,
      inputs: kit ? brandKitToGenerationInputs(kit) : null,
      binding: refreshBinding(binding, { stored: kit }),
      source: 'snapshot',
    }
  }

  const fallback = await fetchDefaultBrandKit()
  return fallback ? landed(fallback, binding, 'default') : NO_KIT
}

function landed(kit: StoredBrandKit, binding: BrandKitBinding | null, source: 'live' | 'default'): KitResolution {
  publishBrandFonts(kit)
  const next = refreshBinding(binding, { live: kit })
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    // The binding rides every deck blob write, so its size is the number to watch when the kit grows.
    console.debug(`[kit] ${source} read of "${kit.name}" → binding ${JSON.stringify(next).length} chars`)
  }
  return { kit, inputs: storedKitToGenerationInputs(kit), binding: next, source }
}

/**
 * Project a STORED kit into generation inputs — {@link brandKitToGenerationInputs} plus the logo merge
 * this file exists for.
 *
 * The mark lives on the entity COLUMN (`logoUrl`/`logoPlacement`), never inside `tokens` — the wire seam
 * strips any copy it finds there. So the projection alone can never produce a logo, and this is the one
 * place the column value is merged back in. Injected AFTER projection, onto the tokens, so the merge has
 * no opinion about how `moodboardToBrandTokens` arbitrates profile vs exact CSS.
 *
 * `logoPlacement === 'OFF'` means off: no logo reaches the prompt even if the column still holds a file
 * (unlink keeps the file served for old decks — that must not resurrect it on new ones).
 */
export function storedKitToGenerationInputs(kit: StoredBrandKit, strength: number | null = 100): BrandKitGenerationInputs {
  const inputs = brandKitToGenerationInputs(kit, strength)
  const logoUrl = kit.logoPlacement !== 'OFF' ? kit.logoUrl : undefined
  if (!logoUrl) return inputs
  const brandStyle: BrandStyleTokens = { ...(inputs.brandStyle ?? {}), logoUrl }
  return { ...inputs, brandStyle }
}

/**
 * What a deck remembers about the kit that styled it — the plan's "reference plus snapshot" binding.
 *
 * The SNAPSHOT is the promise-keeper: "editing a kit never changes a deck you already made" is only true
 * because the deck carries the profile it actually generated with, not a pointer that dereferences to
 * whatever the kit says today. The id is provenance (which kit, for re-generate); the snapshot is truth.
 * It also keeps public shares self-contained: a viewer with no authenticated owner still has the style.
 */
export interface BrandKitBinding {
  kitId: string
  kitName: string
  /** The profile as generated with. Never re-resolved from the kit. */
  profile: StoredBrandKit['profile']
  /** The exact-CSS side, when the kit had one (logo already merged, placement already applied). */
  brandStyle?: BrandStyleTokens
  /**
   * The kit's TYPE SCALE and its real `@font-face` files, snapshotted beside `brandStyle` rather than
   * inside it.
   *
   * They live here because `BrandStyleTokens` cannot hold them — it is the prompt-facing shape and
   * carries a derived `headingFontWeights` where a kit carries the faces those weights were read from.
   * That mismatch is why both fields used to die at this crossing, silently: a deck generated from the
   * default kit got its palette and its logo and then invented its own type sizes, because the only
   * thing describing the brand's hierarchy never arrived. Colour crossed, hierarchy did not, and the
   * result looked brand-coloured and brand-less at the same time.
   *
   * Snapshotted, like everything else on a binding: editing a kit must not restyle a deck you already
   * made.
   */
  typeScale?: BrandStyle['typeScale']
  fontFaces?: BrandStyle['fontFaces']
  /**
   * Whether the mark reads on a LIGHT background, as the user chose it by looking.
   *
   * Recorded on the binding rather than re-read from the kit because the contrast rule must apply the
   * same way to a slide generated later on this deck as it did to the deck itself. Absent when the user
   * never chose, and absence DISABLES the rule: without knowing which way the mark reads there is no
   * honest contrast judgement to make, and guessing would drop marks invisibly.
   */
  logoOnLight?: boolean
  /** Epoch ms of the generation this binding records. */
  at: number
}

/**
 * The bake-time brand-mark rule for a deck's binding, or undefined when there is nothing to enforce.
 *
 * Undefined in two cases, both correct: the kit has no mark (placement OFF already stripped it upstream),
 * or the user never chose which background it reads on. In both the image walk bakes every image normally.
 */
export function bakeBrandLogoFrom(binding: BrandKitBinding | null | undefined): BakeBrandLogo | undefined {
  if (!binding?.brandStyle?.logoUrl) return undefined
  if (binding.logoOnLight == null) return undefined
  return { logoOnLight: binding.logoOnLight }
}

/**
 * The profile as a binding carries it: the kit's SYSTEM, without its library.
 *
 * A binding rides every deck blob write, so what stays on the live kit is everything that is a store of
 * documents or renders rather than a decision about the brand: the reference documents (nine of them are
 * 60-180KB of HTML), the traced pages (up to 20 more, ~400KB measured 2026-09-05), the PDF page index
 * (160 entries) and the site capture. `stripPageIndex` and `stripTraces` existed and were called by
 * nothing, so all of that rode into every deck until now.
 *
 * WHAT STAYS, ON PURPOSE: `compositionRoles`, `logoVariations`, `colorGroups`, `rules`, `toneExamples`.
 * A brand's own compositions and its standing rules ARE the system a public share or a signed-out tab
 * generates against when the live kit is unreachable; stripping them would make the fallback a different
 * brand. `stripKitRoles` was deleted for that reason. Pinned by a test.
 */
export function bindingProfile(profile: StoredBrandKit['profile']): StoredBrandKit['profile'] {
  const clone = JSON.parse(JSON.stringify(profile)) as StoredBrandKit['profile']
  return stripKitDocument(stripTraces(stripPageIndex(stripSiteCapture(stripReferences(clone)))))
}

export function bindingFromKit(kit: StoredBrandKit, inputs: BrandKitGenerationInputs): BrandKitBinding {
  return {
    kitId: kit.id,
    kitName: kit.name,
    profile: bindingProfile(kit.profile),
    ...(inputs.brandStyle ? { brandStyle: { ...inputs.brandStyle } } : {}),
    // Straight off the KIT, not off `inputs`: the projection to `BrandStyleTokens` has no room for either
    // (see the binding's own note), so reading them from there would snapshot undefined every time.
    ...(kit.brandStyle?.typeScale ? { typeScale: JSON.parse(JSON.stringify(kit.brandStyle.typeScale)) } : {}),
    ...(kit.brandStyle?.fontFaces?.length ? { fontFaces: kit.brandStyle.fontFaces.map((f) => ({ ...f })) } : {}),
    ...(kit.logoOnLight != null ? { logoOnLight: kit.logoOnLight } : {}),
    at: Date.now(),
  }
}
